/**
 * AIアシスタントのグローバル状態管理
 *
 * ページ遷移を跨いで会話履歴を維持するためのContext
 */

import {
  createContext,
  useContext,
  useState,
  useCallback,
  useRef,
  useEffect,
  ReactNode,
} from "react";
import { useLocation } from "react-router-dom";
import {
  sendMessage,
  fetchSessions,
  fetchSessionDetail,
  PageContext,
  ChatResponse,
  SessionSummary,
  SessionDetail,
  Message,
  AttachedImage,
} from "../services/assistantService";
import { SESSION_TIMEOUT_MS, MAX_PAGE_CONTENT_LENGTH } from "../constants/app";
import { onAuthStateChanged } from "firebase/auth";
import { getAuthInstance } from "../lib/firebase";


/**
 * メインコンテンツエリアのテキストを取得
 *
 * 1. data-ai-guide 要素があれば、その内容を優先（開発者が記載したセマンティック情報）
 * 2. なければ data-assistant-context 内の innerText をフォールバックとして使用
 * 3. モーダルが開いている場合はその内容も追加
 */
function getPageContent(): string {
  const parts: string[] = [];

  // 1. AIガイド要素を優先取得（ページごとに設定されたセマンティック情報）
  const aiGuide = document.querySelector("[data-ai-guide]");
  if (aiGuide) {
    parts.push((aiGuide as HTMLElement).innerText.trim());
  } else {
    // フォールバック: メインコンテンツのinnerTextを取得
    const mainContent = document.querySelector("[data-assistant-context]");
    if (mainContent) {
      parts.push((mainContent as HTMLElement).innerText || "");
    }
  }

  // 2. モーダルが開いている場合はその内容も追加
  const modalContent = document.querySelector(".mantine-Modal-content");
  if (modalContent) {
    const modalText = (modalContent as HTMLElement).innerText || "";
    if (modalText) {
      parts.push(`\n[ダイアログ内容]\n${modalText}`);
    }
  }

  const text = parts.join("\n");

  // 長すぎる場合は切り詰め
  if (text.length > MAX_PAGE_CONTENT_LENGTH) {
    return text.substring(0, MAX_PAGE_CONTENT_LENGTH) + "\n...(省略されました)";
  }

  return text;
}

/**
 * 現在のページコンテキストを取得
 */
function getPageContext(pathname: string): PageContext {
  return {
    path: pathname,
    data: {
      pageContent: getPageContent(),
    },
  };
}

/**
 * AIアシスタントのステート
 */
export interface AssistantState {
  // パネル表示
  panelOpened: boolean;
  
  // チャット関連
  messages: Message[];
  isLoading: boolean;
  error: string | null;

  // セッション関連
  currentSessionId: string | null;
  sessions: SessionSummary[];
  isLoadingSessions: boolean;
}

/**
 * AIアシスタントのアクション
 */
export interface AssistantActions {
  // パネル操作
  openPanel: () => void;
  closePanel: () => void;
  togglePanel: () => void;
  
  // チャット操作
  sendChat: (message: string, images?: AttachedImage[]) => Promise<void>;
  clearError: () => void;

  // セッション操作
  loadSessions: () => Promise<void>;
  selectSession: (sessionId: string) => Promise<void>;
  startNewSession: () => void;
}

type AssistantContextValue = AssistantState & AssistantActions;

const AssistantContext = createContext<AssistantContextValue | null>(null);

/**
 * AssistantProvider
 *
 * アプリ全体でアシスタント状態を共有するためのプロバイダー
 */
export function AssistantProvider({ children }: { children: ReactNode }) {
  const [uid, setUid] = useState(() => getAuthInstance().currentUser?.uid ?? "signed-out");
  useEffect(() => onAuthStateChanged(getAuthInstance(), user => setUid(user?.uid ?? "signed-out")), []);
  return <UserAssistantProvider key={uid}>{children}</UserAssistantProvider>;
}

function UserAssistantProvider({ children }: { children: ReactNode }) {
  const requests = useRef(new AbortController());
  useEffect(() => {
    requests.current = new AbortController();
    return () => requests.current.abort();
  }, []);
  // パネル表示状態（localStorageで永続化）
  const [panelOpened, setPanelOpened] = useState(() => {
    try {
      const saved = localStorage.getItem("assistant-panel-opened");
      return saved === "true";
    } catch {
      return false;
    }
  });
  
  // ステート
  const [messages, setMessages] = useState<Message[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [currentSessionId, setCurrentSessionId] = useState<string | null>(null);
  const [sessions, setSessions] = useState<SessionSummary[]>([]);
  const [isLoadingSessions, setIsLoadingSessions] = useState(false);

  // 現在のパスを取得（レンダリングへの影響を最小限にするためrefで保持）
  const location = useLocation();
  const pathnameRef = useRef(location.pathname);
  pathnameRef.current = location.pathname;

  // 最終アクティビティ時刻
  const lastActivityRef = useRef<number>(Date.now());

  /**
   * セッションがタイムアウトしているかチェック
   */
  const isSessionTimedOut = useCallback(() => {
    return Date.now() - lastActivityRef.current > SESSION_TIMEOUT_MS;
  }, []);

  /**
   * メッセージを送信
   */
  const sendChat = useCallback(
    async (message: string, images?: AttachedImage[]) => {
      if (!message.trim()) return;

      setIsLoading(true);
      setError(null);

      // タイムアウトチェック
      let sessionId = currentSessionId;
      if (sessionId && isSessionTimedOut()) {
        sessionId = null;
        setCurrentSessionId(null);
        setMessages([]);
      }

      // ユーザーメッセージを画面に追加（楽観的更新）
      // 画像をdata URL形式に変換してチャット欄で表示
      const imageUrls = images?.map(
        (img) => `data:${img.mimeType};base64,${img.data}`
      );
      const userMessage: Message = {
        role: "user",
        content: message,
        timestamp: new Date().toISOString(),
        hasImages: images && images.length > 0,
        imageUrls,
      };
      setMessages((prev) => [...prev, userMessage]);

      try {
        // 送信時にページコンテキストを動的に取得
        const pageContext = getPageContext(pathnameRef.current);
        
        const response: ChatResponse = await sendMessage({
          sessionId: sessionId ?? undefined,
          message,
          pageContext,
          images,
        }, requests.current.signal);

        // AIの応答を追加
        const assistantMessage: Message = {
          role: "assistant",
          content: response.response,
          timestamp: new Date().toISOString(),
        };
        setMessages((prev) => [...prev, assistantMessage]);

        // セッションIDを更新
        if (response.createdNewSession || !currentSessionId) {
          setCurrentSessionId(response.sessionId);
        }

        // アクティビティ時刻を更新
        lastActivityRef.current = Date.now();
      } catch (err) {
        // エラー時はユーザーメッセージを削除
        setMessages((prev) => prev.slice(0, -1));
        setError(err instanceof Error ? err.message : "エラーが発生しました");
      } finally {
        setIsLoading(false);
      }
    },
    [currentSessionId, isSessionTimedOut]
  );

  /**
   * セッション一覧を読み込み
   */
  const loadSessions = useCallback(async () => {
    setIsLoadingSessions(true);
    try {
      const sessionList = await fetchSessions(requests.current.signal);
      setSessions(sessionList);
    } catch (err) {
      console.error("セッション一覧の取得に失敗:", err);
    } finally {
      setIsLoadingSessions(false);
    }
  }, []);

  /**
   * セッションを選択
   */
  const selectSession = useCallback(async (sessionId: string) => {
    setIsLoading(true);
    try {
      const detail: SessionDetail = await fetchSessionDetail(sessionId, requests.current.signal);
      setCurrentSessionId(detail.id);
      setMessages(detail.messages);
      lastActivityRef.current = Date.now();
    } catch (err) {
      setError(
        err instanceof Error ? err.message : "セッションの読み込みに失敗しました"
      );
    } finally {
      setIsLoading(false);
    }
  }, []);

  /**
   * 新規セッションを開始
   */
  const startNewSession = useCallback(() => {
    setCurrentSessionId(null);
    setMessages([]);
    setError(null);
  }, []);

  /**
   * エラーをクリア
   */
  const clearError = useCallback(() => {
    setError(null);
  }, []);

  /**
   * パネルを開く
   */
  const openPanel = useCallback(() => {
    setPanelOpened(true);
    localStorage.setItem("assistant-panel-opened", "true");
  }, []);

  /**
   * パネルを閉じる
   */
  const closePanel = useCallback(() => {
    setPanelOpened(false);
    localStorage.setItem("assistant-panel-opened", "false");
  }, []);

  /**
   * パネルを切り替え
   */
  const togglePanel = useCallback(() => {
    setPanelOpened((prev) => {
      const newValue = !prev;
      localStorage.setItem("assistant-panel-opened", String(newValue));
      return newValue;
    });
  }, []);

  // 注: セッション一覧の読み込みは履歴パネルを開いた時に行う（ここでは認証状態が不明なため）

  const value: AssistantContextValue = {
    // State
    panelOpened,
    messages,
    isLoading,
    error,
    currentSessionId,
    sessions,
    isLoadingSessions,
    // Actions
    openPanel,
    closePanel,
    togglePanel,
    sendChat,
    clearError,
    loadSessions,
    selectSession,
    startNewSession,
  };

  return (
    <AssistantContext.Provider value={value}>
      {children}
    </AssistantContext.Provider>
  );
}

/**
 * アシスタントコンテキストを使用するフック
 */
export function useAssistantContext(): AssistantContextValue {
  const context = useContext(AssistantContext);
  if (!context) {
    throw new Error(
      "useAssistantContext must be used within an AssistantProvider"
    );
  }
  return context;
}
